import { Link } from 'react-router-dom';
import { money } from '../../../lib/api';
import type { CategoryStatus } from '../../../shared/types';
import type { Resource } from '../useResource';
import { monthElapsed } from '../dates';
import { Card, CardTitle, Empty, ErrorState, Icon, LoadingRows, Pill } from '../ui';

const STATE_COPY: Record<CategoryStatus['state'], string> = {
  ok: 'On track',
  near: 'Close to cap',
  over: 'Over cap',
};

/**
 * The month at a glance: everything spent, against every cap, in one bar —
 * segmented by category in that category's own color (from D1), with a marker
 * for how far through the month we actually are. Caps and colors are config;
 * none of it is hardcoded here.
 */
export default function MoneyCard({ res }: { res: Resource<CategoryStatus[]> }) {
  const { data, loading, error, reload } = res;

  return (
    <Card className="p-5 sm:p-6">
      <CardTitle icon="wallet" action={{ to: '/money', label: 'Budget' }}>
        This month
      </CardTitle>

      <div className="mt-4">
        {loading && <LoadingRows rows={4} />}
        {!loading && error && <ErrorState what="the budget" onRetry={reload} />}

        {!loading && !error && data?.length === 0 && (
          <Empty
            message="No spending categories yet. Add the first one and the hub starts tracking this month against it."
            action={{ to: '/admin', label: 'Add a category' }}
          />
        )}

        {!loading && !error && data && data.length > 0 && <Rollup rows={data} />}
      </div>
    </Card>
  );
}

function Rollup({ rows }: { rows: CategoryStatus[] }) {
  const spent = rows.reduce((sum, r) => sum + r.spent, 0);
  const caps = rows.reduce((sum, r) => sum + r.monthly_cap, 0);
  const remaining = caps - spent;
  // Scale to whichever is bigger so an over-cap month still fits in the bar.
  const scale = Math.max(caps, spent, 1);
  const pace = monthElapsed();
  const overCount = rows.filter((r) => r.state === 'over').length;
  const nearCount = rows.filter((r) => r.state === 'near').length;

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
        <p className="font-display text-4xl leading-none font-semibold text-ink sm:text-5xl">
          {money(spent)}
        </p>
        <p className="text-sm text-ink-soft">
          of <span className="font-medium text-ink">{money(caps)}</span> in caps
        </p>
      </div>

      <p className={`mt-1.5 text-sm ${remaining < 0 ? 'font-medium text-over' : 'text-ink-soft'}`}>
        {remaining >= 0
          ? `${money(remaining)} left for the rest of the month`
          : `${money(-remaining)} over the household caps`}
      </p>

      {/* Segmented spend bar. The numbers above and the rows below carry the same
          information in text — the bar is a fast read, not the only read. */}
      <div
        className="relative mt-4 h-3.5 w-full overflow-hidden rounded-full bg-surface-sunk"
        role="img"
        aria-label={`${money(spent)} spent of ${money(caps)} in monthly caps`}
      >
        <div className="flex h-full w-full">
          {rows
            .filter((r) => r.spent > 0)
            .map((r) => (
              <div
                key={r.id}
                className="h-full first:rounded-l-full"
                style={{ width: `${(r.spent / scale) * 100}%`, backgroundColor: r.color }}
              />
            ))}
        </div>
        <div
          className="absolute inset-y-0 w-0.5 bg-ink/35"
          style={{ left: `${Math.min(100, (pace * caps) / scale * 100)}%` }}
          aria-hidden="true"
        />
      </div>
      <p className="mt-1.5 text-xs text-ink-faint">
        The line is today&rsquo;s pace — spending left of it is ahead of the month.
      </p>

      {spent === 0 && (
        <div className="mt-4 rounded-xl bg-surface-sunk p-4">
          <Empty
            message="Nothing spent yet this month. Add this month's first transaction and every card here fills in."
            action={{ to: '/money/transactions', label: 'Add a transaction' }}
          />
        </div>
      )}

      <ul className="mt-5 space-y-3">
        {rows.map((r) => (
          <li key={r.id} className="flex items-center gap-3">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: r.color }}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{r.name}</span>
            <span className="text-sm text-ink-soft tabular-nums">
              {money(r.spent)}{' '}
              <span className="text-ink-faint">/ {money(r.monthly_cap)}</span>
            </span>
            <Pill tone={r.state}>{STATE_COPY[r.state]}</Pill>
          </li>
        ))}
      </ul>

      {(overCount > 0 || nearCount > 0) && (
        <Link
          to="/money"
          className="mt-4 inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-clay hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          {overCount > 0
            ? `${overCount} ${overCount === 1 ? 'category is' : 'categories are'} over cap`
            : `${nearCount} ${nearCount === 1 ? 'category is' : 'categories are'} close to cap`}
          <Icon name="arrow" className="h-4 w-4" />
        </Link>
      )}
    </>
  );
}
